import { YesNoTag } from "./enums";

export class QualificationCategoryType {
    readonly id: number;
    readonly description: string;

    constructor(id: number, description: string) {
        this.id = id;
        this.description = description;
    }

    public static Skill = new QualificationCategoryType(3, "Skills");
    public static License = new QualificationCategoryType(2, "license");
    public static Education = new QualificationCategoryType(1, "Education");
    public static CustomQuestion = new QualificationCategoryType(7, "custom");
    public static Video = new QualificationCategoryType(8, "Video");

    public static asArray = () => [
        QualificationCategoryType.Skill,
        QualificationCategoryType.CustomQuestion,
        QualificationCategoryType.License,
    ];

    public static fromId = (id: number) =>
        QualificationCategoryType.asArray().find(i => i.id === id);
}

export class Qualification {
    readonly id: number;
    readonly qualificationCategory: QualificationCategoryType;
    readonly choiceTagTypeId: number | null;
    readonly contextTagTypeId: number | null;

    constructor(
        id: number,
        ordinal: number,
        qualificationCategory: QualificationCategoryType,
        choiceTagTypeId: number | null,
        contextTagTypeId: number | null
    ) {
        this.id = id;
        this.qualificationCategory = qualificationCategory;
        this.choiceTagTypeId = choiceTagTypeId;
        this.contextTagTypeId = contextTagTypeId;
    }

    public static SkillAllOf = new Qualification(
        40,
        4,
        QualificationCategoryType.Skill,
        230,
        240
    );

    public static SkillAnyOf = new Qualification(
        45,
        5,
        QualificationCategoryType.Skill,
        230,
        240
    );

    public static LicenseAllOf = new Qualification(
        20,
        2,
        QualificationCategoryType.License,
        210,
        220
    );

    public static LicenseAnyOf = new Qualification(
        25,
        6,
        QualificationCategoryType.License,
        210,
        220
    );

    // public static LicenseWithState = new Qualification(
    //     30,
    //     3,
    //     QualificationCategoryType.License,
    //     210,
    //     220,
    //     "Do you have a valid _____ license/certification in the state of _____?"
    // );

    // public static LicenseAnyOfWithState = new Qualification(
    //     35,
    //     7,
    //     QualificationCategoryType.License,
    //     210,
    //     220,
    //     "Do you have any of the following valid _____ licenses/certifications in the state of _____?"
    // );

    public static Education = new Qualification(
        10,
        1,
        QualificationCategoryType.Education,
        200,
        null
    );

    public static CustomYesNo = new Qualification(
        80,
        9,
        QualificationCategoryType.CustomQuestion,
        280,
        null
    );

    public static CustomText = new Qualification(
        90,
        10,
        QualificationCategoryType.CustomQuestion,
        null,
        null
    );

    public static Video = new Qualification(
        100,
        11,
        QualificationCategoryType.CustomQuestion,
        null,
        null
    );

    public isSkillAllOf = () => this === Qualification.SkillAllOf;
    public isSkillAnyOf = () => this === Qualification.SkillAnyOf;
    public isSkill = () => this.isSkillAllOf() || this.isSkillAnyOf();
    public isEducation = () => this === Qualification.Education;
    public isCustomYesNo = () => this === Qualification.CustomYesNo;
    public isCustomText = () => this === Qualification.CustomText;
    public isCustom = () => this.isCustomText() || this.isCustomYesNo();
    public isLicenseAllOf = () => this === Qualification.LicenseAllOf;
    public isVideo = () => this === Qualification.Video;
    public isLicenseAnyOf = () => this === Qualification.LicenseAnyOf;

    public static asArray = () => [
        Qualification.Education,
        Qualification.SkillAllOf,
        Qualification.SkillAnyOf,
        Qualification.CustomYesNo,
        Qualification.CustomText,
        Qualification.LicenseAllOf,
        Qualification.LicenseAnyOf,
        Qualification.Video,
    ];

    public static byQualificationCategory = (cat: QualificationCategoryType) =>
        Qualification.asArray().filter(q => q.qualificationCategory === cat);

    public static fromId = (id: number) =>
        Qualification.asArray().find(q => q.id === id);
}

export interface IJobQualification {
    id: number;
    qualification: Qualification;
    isRequired: boolean;
    type: string;
    ordinal: number;
}

export interface SkillChoice {
    choiceId: number;
    customText?: string;
    tagId: number;
    text: string;
}
export interface SkillJobQualification extends IJobQualification {
    type: "Skills";

    yearsExperience: YearsExperience;
    skills: SkillChoice[];
}
export interface LicenseJobQualification extends IJobQualification {
    type: "License";

    state: string;
    licenses: Array<{
        tagId: number;
        text: string;
    }>;
}

export interface LicenseAnyOfJobQualification extends IJobQualification {
    type: "LicenseAnyOf";
    state: string;
    licenses: Array<{
        tagId: number;
        text: string;
    }>;
}

export interface CustomYesNoJobQualification extends IJobQualification {
    type: "CustomYesNo";
    question: string;
    desiredAnswer: {
        choiceId: number;
        answer: YesNoTag;
    };
}

export interface CustomTextJobQualification extends IJobQualification {
    type: "CustomText";
    question: string;
}

export type JobQualificationTypes =
    | SkillJobQualification
    | LicenseJobQualification
    | LicenseAnyOfJobQualification;
export class YearsExperience {
    readonly id: number | undefined;
    readonly description: string;
    readonly value: number;

    constructor(id: number | undefined, description: string, value: number) {
        this.id = id;
        this.description = description;
        this.value = value;
    }

    public static Zero = new YearsExperience(0, "0", 0);
    public static One = new YearsExperience(3276, "1", 1);
    public static Two = new YearsExperience(3277, "2", 2);
    public static Three = new YearsExperience(3278, "3", 3);
    public static Four = new YearsExperience(3279, "4", 4);
    public static Five = new YearsExperience(3280, "5", 5);
    public static Six = new YearsExperience(3281, "6", 6);
    public static Seven = new YearsExperience(3282, "7", 7);
    public static Eight = new YearsExperience(3283, "8", 8);
    public static Nine = new YearsExperience(3284, "9", 9);
    public static Ten = new YearsExperience(3285, "10+", 10);

    public static AsArrayAll = () => [
        YearsExperience.Zero,
        YearsExperience.One,
        YearsExperience.Two,
        YearsExperience.Three,
        YearsExperience.Four,
        YearsExperience.Five,
        YearsExperience.Six,
        YearsExperience.Seven,
        YearsExperience.Eight,
        YearsExperience.Nine,
        YearsExperience.Ten,
    ];

    public static asArrayConfigure = () =>
        YearsExperience.AsArrayAll().filter(ye => ye.id !== undefined);

    public static fromId = (id: number) =>
        YearsExperience.AsArrayAll().find(ye => ye.id === id);

    public static fromValue = (value: number) =>
        YearsExperience.AsArrayAll().find(y => y.value === value);
}
